import React, { useEffect, useMemo } from 'react';
import {
    DetailDossierTitle,
    DetailDossierToolbar,
    useCurrentDossierCtx,
    useLazyDataLoader,
} from '@europrocurement/l2d-modules';

import { useTiersCtx } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';

/**
 * Composant header du détail d'un dossier.
 * Gère le chargement progressif des données (prescripteur, offre, client opéré)
 * et affiche un skeleton durant le chargement.
 */
const DetailDossierHeader: React.FunctionComponent = function () {
    const { dossier, siblings } = useCurrentDossierCtx();
    const { loadTiers, refreshTiers } = useLazyDataLoader();
    const { getTiersById } = useTiersCtx();

    /** Données du dossier formatées pour l'affichage */
    const formattedDossier = useMemo(
        () => ({
            statut: dossier?.statutLibelle as string,
            societeRef: dossier?.societeRef as string,
            statutId: dossier?.statutId as number,
            id: dossier?.id as number,
            collectionId: dossier?.collectionId as number,
        }),
        [
            dossier?.statutLibelle,
            dossier?.societeRef,
            dossier?.statutId,
            dossier?.id,
            dossier?.collectionId,
        ],
    );

    /** Données de l'offre formatées pour l'affichage */
    const formattedOffre = useMemo(
        () => ({
            libelle: dossier?.offreLibelle || 'Aucune offre',
        }),
        [dossier?.offreLibelle],
    );

    const isMultiDossiers = siblings && siblings.length > 1;

    useEffect(() => {
        const allIds: number[] = [];
        [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]
            .filter((id) => id !== undefined)
            .reduce((accumulator, currentId: number) => {
                if (!accumulator.includes(currentId)) {
                    accumulator.push(currentId);
                }

                return accumulator;
            }, allIds)
            .map((tiersId) => loadTiers(tiersId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.prescripteurId, dossier?.clientOpereId, dossier?.clientId]);

    if (!dossier) {
        return null;
    }

    const prescripteur = dossier?.prescripteurId
        ? getTiersById(dossier?.prescripteurId)
        : undefined;
    const clientOpere = dossier?.clientOpereId ? getTiersById(dossier?.clientOpereId) : undefined;

    if (!prescripteur || !clientOpere) {
        return null;
    }

    const formattedPrescripteur = {
        id: prescripteur?.id as number,
        raisonSociale: (prescripteur?.raisonSociale as string) || 'Pas de raison sociale',
        codeComptable: prescripteur?.codeComptable || 'Pas de code comptable',
        /** URL vers la page de détail du prescripteur */
        urlToDetails: `/${prescripteursBasePath}/liste/${dossier?.prescripteurId}`,
    };

    const formattedClientOpere = {
        raisonSociale: clientOpere?.raisonSociale || 'Pas de raison sociale',
        siren: clientOpere?.siren,
        urlToSocieteDotCom: `https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere?.siren}`,
        id: clientOpere?.id as number,
    };

    return (
        <>
            {/* Toolbar affichée dès que dossier et siblings sont disponibles */}
            <DetailDossierToolbar
                dossier={dossier}
                source="BACK"
                siblingDossiers={siblings}
                isMultiDossiers={isMultiDossiers}
            />

            {/* Title affiché seulement quand toutes les données sont prêtes */}
            <DetailDossierTitle
                dossier={formattedDossier}
                prescripteur={formattedPrescripteur}
                offre={formattedOffre}
                clientOpere={formattedClientOpere}
                color="primary"
                icon={isMultiDossiers ? 'folders' : 'folder'}
                onEditSousClientSuccess={() => refreshTiers(clientOpere.id)}
            />
        </>
    );
};

export default DetailDossierHeader;
